import React, { useEffect, useState } from 'react';
import {
  Box, Button, Container, TextField, Typography, CircularProgress,
  Alert, List, ListItem, ListItemText, IconButton, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const API_URL = process.env.REACT_APP_API_URL;

const estadosBrasil = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];

const ArquivoItem = ({ arquivo, onDelete, clienteUuid }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  
  const tipo = arquivo.tipo || 'desconhecido';
  const dataEnvio = arquivo.data_envio ? new Date(arquivo.data_envio).toLocaleString() : 'Data desconhecida';

  // Função para buscar URL assinada do Spaces
  const buscarUrlAssinada = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/clientes/${clienteUuid}/arquivos/${arquivo.id}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setUrl(res.data.url);
    } catch (err) {
      console.error('❌ Erro ao buscar URL:', err);
    } finally {
      setLoading(false);
    }
  };

  // Buscar URL ao montar componente
  useEffect(() => {
    buscarUrlAssinada();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ListItem
      sx={{
        flexDirection: 'column',
        alignItems: 'flex-start',
        mb: 2,
        p: 2,
        border: '1px solid #ddd',
        borderRadius: 2,
        backgroundColor: '#fafafa'
      }}
      secondaryAction={
        <IconButton edge="end" onClick={() => onDelete(arquivo.id)}>
          <DeleteIcon />
        </IconButton>
      }
    >
      <ListItemText
        primary={<strong>{arquivo.nome}</strong>}
        secondary={`Tipo: ${tipo} | Enviado em: ${dataEnvio}`}
      />

      {loading && <CircularProgress size={24} sx={{ mt: 2 }} />}

      {!loading && url && tipo.startsWith('image/') && (
        <img
          src={url}
          alt={arquivo.nome}
          style={{
            width: '100%',
            maxHeight: 400,
            marginTop: 10,
            objectFit: 'contain',
            borderRadius: 8
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            console.error('❌ Imagem falhou:', url);
          }}
        />
      )}

      {!loading && url && tipo === 'application/pdf' && (
        <iframe
          src={url}
          title={arquivo.nome}
          width="100%"
          height="500px"
          style={{ border: '1px solid #ccc', marginTop: 10, borderRadius: 8 }}
        />
      )}

      {!loading && url && !tipo.startsWith('image/') && tipo !== 'application/pdf' && (
        <Button
          variant="outlined"
          href={url}
          target="_blank"
          sx={{
            mt: 2,
            borderColor: '#ed3237',
            color: '#ed3237',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#ed3237',
              color: '#fff'
            }
          }}
        >
          Visualizar / Download
        </Button>
      )}
    </ListItem>
  );
};

export default function EditarCliente() {
  const { uuid } = useParams();
  const navigate = useNavigate();

  const [cliente, setCliente] = useState(null);
  const [arquivos, setArquivos] = useState([]);
  const [novosArquivos, setNovosArquivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
const [snackbar, setSnackbar] = useState({
  open: false,
  message: '',
  severity: 'success' // 'success' | 'error' | 'warning' | 'info'
});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resCliente = await axios.get(`${API_URL}/clientes/${uuid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCliente(resCliente.data);

        const resArquivos = await axios.get(`${API_URL}/clientes/${uuid}/arquivos`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setArquivos(resArquivos.data || []);
      } catch (err) {
        console.error(err);
        setError('Erro ao carregar dados.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [uuid, token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCliente({ ...cliente, [name]: value });
  };

  const handleFileChange = (e) => {
    setNovosArquivos(Array.from(e.target.files));
  };

 const handleFileDelete = async (arquivoId) => {
  try {
    await axios.delete(`${API_URL}/clientes/${uuid}/arquivos/${arquivoId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setArquivos(prev => prev.filter(a => a.id !== arquivoId));

    setSnackbar({
      open: true,
      message: '🗑️ Arquivo deletado com sucesso!',
      severity: 'success'
    });
  } catch (err) {
    console.error('Erro ao deletar arquivo:', err);
    setSnackbar({
      open: true,
      message: '❌ Erro ao deletar arquivo',
      severity: 'error'
    });
  }
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API_URL}/clientes/${uuid}`, cliente, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (novosArquivos.length > 0) {
        const formData = new FormData();
        novosArquivos.forEach(file => formData.append('arquivo', file));

        const uploadURL = `${API_URL}/clientes/${uuid}/arquivos`;

        const resUpload = await axios.post(uploadURL, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`📤 Upload progresso: ${progress}%`);
          }
        });

        console.log('✅ Upload completado:', resUpload.data);
      }

setSnackbar({
  open: true,
  message: '✅ Cliente atualizado com sucesso!',
  severity: 'success'
});
      navigate('/cadastros/clientes');
    } catch (err) {
      console.error('❌ Erro ao salvar alterações:', err.response?.data || err);
      alert(`Erro ao salvar alterações: ${err.response?.data?.mensagem || 'Tente novamente.'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box textAlign="center" mt={10}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!cliente) return null;

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', py: 4, px: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        <Box sx={{ backgroundColor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {/* HEADER GRADIENT */}
          <Box sx={{
            background: 'linear-gradient(135deg, #3e4095 0%, #ed3237 100%)',
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: '#fff'
          }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', flex: 1 }}>
              ✏️ EDITAR CLIENTE
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* SEÇÃO 1: DADOS PESSOAIS */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#3e4095', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                👤 DADOS PESSOAIS
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}>
                <TextField
                  fullWidth
                  label="Nome Completo *"
                  name="nome"
                  value={cliente?.nome || ''}
                  onChange={handleInputChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      '&:hover fieldset': { borderColor: '#ed3237' },
                      '&.Mui-focused fieldset': { borderColor: '#ed3237', borderWidth: 2 }
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#ed3237' }
                  }}
                />
                <TextField
                  fullWidth
                  label="CPF/CNPJ *"
                  name="cpf_cnpj"
                  value={cliente?.cpf_cnpj || ''}
                  onChange={handleInputChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      '&:hover fieldset': { borderColor: '#ed3237' },
                      '&.Mui-focused fieldset': { borderColor: '#ed3237', borderWidth: 2 }
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#ed3237' }
                  }}
                />
              </Box>
            </Box>

            {/* SEÇÃO 2: CONTATO */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#3e4095', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📞 CONTATO
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}>
                <TextField
                  fullWidth
                  label="Telefone *"
                  name="telefone"
                  value={cliente?.telefone || ''}
                  onChange={handleInputChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      '&:hover fieldset': { borderColor: '#ed3237' },
                      '&.Mui-focused fieldset': { borderColor: '#ed3237', borderWidth: 2 }
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#ed3237' }
                  }}
                />
                <TextField
                  fullWidth
                  label="Email *"
                  name="email"
                  type="email"
                  value={cliente?.email || ''}
                  onChange={handleInputChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      '&:hover fieldset': { borderColor: '#ed3237' },
                      '&.Mui-focused fieldset': { borderColor: '#ed3237', borderWidth: 2 }
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#ed3237' }
                  }}
                />
              </Box>
            </Box>

            {/* SEÇÃO 3: ENDEREÇO */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#3e4095', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📍 ENDEREÇO
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 200px' },
                gap: 2
              }}>
                <TextField
                  fullWidth
                  label="Endereço *"
                  name="endereco"
                  value={cliente?.endereco || ''}
                  onChange={handleInputChange}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fff',
                      '&:hover fieldset': { borderColor: '#ed3237' },
                      '&.Mui-focused fieldset': { borderColor: '#ed3237', borderWidth: 2 }
                    },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#ed3237' }
                  }}
                />
                <FormControl fullWidth required>
                  <InputLabel sx={{ '&.Mui-focused': { color: '#ed3237' } }}>UF *</InputLabel>
                  <Select
                    name="uf"
                    value={cliente?.uf || ''}
                    onChange={handleInputChange}
                    label="UF *"
                    sx={{
                      backgroundColor: '#fff',
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ed3237' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ed3237', borderWidth: 2 }
                    }}
                  >
                    {estadosBrasil.map((uf) => (
                      <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* SEÇÃO 4: ARQUIVOS EXISTENTES */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#3e4095', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📂 ARQUIVOS ENVIADOS
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0'
              }}>
                {arquivos.length === 0 ? (
                  <Typography color="textSecondary">Nenhum arquivo enviado ainda.</Typography>
                ) : (
                  <List disablePadding>
                    {arquivos.map((arquivo) => (
                      <ArquivoItem key={arquivo.id} arquivo={arquivo} onDelete={handleFileDelete} clienteUuid={uuid} />
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            {/* SEÇÃO 5: ADICIONAR NOVOS ARQUIVOS */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#3e4095', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📎 ADICIONAR ARQUIVOS (Opcional)
              </Typography>
              <Box sx={{ 
                backgroundColor: '#f8f9fa', 
                p: 3, 
                borderRadius: 1.5, 
                border: '1px solid #e0e0e0'
              }}>
                <Button
                  component="label"
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #3e4095 0%, #ed3237 100%)',
                    color: '#fff',
                    fontWeight: 'bold',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0d0d0d 0%, #e67800 100%)'
                    }
                  }}
                >
                  Selecionar Arquivos
                  <input type="file" hidden multiple onChange={handleFileChange} />
                </Button>

                {novosArquivos.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 2, color: '#666', fontWeight: 500 }}>
                    ✓ {novosArquivos.length} arquivo(s) selecionado(s) para envio
                  </Typography>
                )}
              </Box>
            </Box>

            {/* BOTÕES */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/cadastros/clientes')}
                sx={{
                  borderColor: '#999',
                  color: '#999',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
              >
                VOLTAR
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                sx={{
                  background: 'linear-gradient(135deg, #3e4095 0%, #ed3237 100%)',
                  color: '#fff',
                  fontWeight: 'bold',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0d0d0d 0%, #e67800 100%)'
                  }
                }}
              >
                {saving ? 'SALVANDO...' : 'SALVAR'}
              </Button>
            </Box>
          </Box>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <MuiAlert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} variant="filled">
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Container>
    </Box>
  );
}
